import { useCallback, useEffect, useState } from 'react';
import { Activity, HardDrive, RefreshCcw, ShieldCheck } from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import CarteIntegration from '@/features/telemetrie/components/CarteIntegration';
import { dateCourte, getTelemetrie } from '@/shared/api/telemetrie';

/**
 * Page de télémétrie — l'état réel des six intégrations, du disque et des certificats.
 *
 * À QUOI ELLE SERT, ET À QUOI ELLE NE SERT PAS
 *
 * Elle ne remplace pas le monitoring d'infrastructure : celui-ci vit chez un service
 * EXTERNE, parce qu'une machine ne peut pas signaler sa propre mort. Cette page-ci
 * répond à l'autre moitié de la question, celle qu'aucune sonde externe ne peut poser :
 * l'application tourne, mais ses cinq raccordements produisent-ils encore ?
 *
 * ELLE EST DÉLIBÉRÉMENT MANUELLE. Pas de rafraîchissement automatique : chaque
 * chargement sonde Tuya, l'espace disque et deux certificats TLS par connexion
 * réelle. Une page qui se recharge toute seule toutes les trente secondes ferait payer
 * ces allers-retours à des partenaires qui facturent au quota, pour un écran que
 * personne ne regarde en continu.
 */

const teinteSysteme = (statut) => ({
  ok: 'bg-emerald-100 text-emerald-700',
  alerte: 'bg-red-100 text-red-700',
  inconnu: 'bg-slate-200 text-slate-700',
}[statut] || 'bg-slate-100 text-slate-700');

const LIBELLES_VEILLE = {
  ok: 'Veille Tuya active',
  demarrage: 'Veille Tuya au démarrage',
  muette: 'Veille Tuya muette',
  arretee: 'Veille Tuya arrêtée',
  inactif: 'Veille Tuya inactive',
};

export default function TelemetriePage() {
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [vuA, setVuA] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur('');
    try {
      setDonnees(await getTelemetrie());
      setVuA(new Date().toISOString());
    } catch (e) {
      console.error(e);
      setErreur("La télémétrie n'a pas répondu. L'API est-elle debout ?");
    }
    setChargement(false);
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const systeme = donnees?.systeme;
  const disque = systeme?.disque;

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="telemetrie-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
            <Activity className="h-5 w-5 text-emerald-600" />
            Télémétrie
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            Pour chaque intégration : est-elle joignable, et surtout <strong>produit-elle
            encore</strong> ? Un service qui répond 200 sans rien produire reste muet —
            c&apos;est cette panne-là que la colonne « heartbeat » débusque.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {vuA && <span className="text-xs text-slate-400">Relevé {dateCourte(vuA)}</span>}
          <Button variant="outline" onClick={charger} disabled={chargement}
                  data-testid="telemetrie-recharger">
            <RefreshCcw className={`mr-2 h-4 w-4 ${chargement ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {erreur && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
           data-testid="telemetrie-erreur">{erreur}</p>
      )}

      {chargement && !donnees && <p className="text-sm text-slate-500">Relevé en cours…</p>}

      {donnees && (
        <>
          {/* `items-start` : sans lui, la carte Tuya + MQTT — seule a porter deux volets —
                imposait sa hauteur a toute sa rangee, et ses voisines affichaient
                un grand vide sous leur contenu. */}
          <div className="grid items-start gap-4 md:grid-cols-2 xl:grid-cols-3">
            {donnees.integrations.map((carte) => (
              <CarteIntegration key={carte.cle} carte={carte} />
            ))}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Card data-testid="carte-disque">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <HardDrive className="h-4 w-4 text-slate-500" />
                  Espace disque
                </CardTitle>
                <p className="text-xs text-slate-500">
                  Mongo cesse d&apos;écrire quand la partition est pleine, et
                  l&apos;application répond encore : on perd des données sans page blanche
                  pour le signaler.
                </p>
              </CardHeader>
              <CardContent>
                {disque?.statut === 'inconnu' ? (
                  <p className="text-sm text-slate-500">Illisible : {disque.detail}</p>
                ) : (
                  <div className="flex items-baseline gap-3">
                    <Badge className={teinteSysteme(disque?.statut)} data-testid="disque-statut">
                      {disque?.libre_pct} % libres
                    </Badge>
                    <span className="text-sm text-slate-600">
                      {disque?.libre_go} Go sur {disque?.total_go} Go
                    </span>
                    <span className="text-xs text-slate-400">
                      alerte sous {disque?.seuil_pct} %
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card data-testid="carte-certificats">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <ShieldCheck className="h-4 w-4 text-slate-500" />
                  Certificats TLS
                </CardTitle>
                <p className="text-xs text-slate-500">
                  Lus par connexion réelle, pas dans un fichier : un renouvellement que
                  nginx n&apos;a pas rechargé laisse un fichier neuf et un certificat
                  périmé servi en ligne.
                </p>
              </CardHeader>
              <CardContent className="space-y-2">
                {systeme.certificats.map((c) => (
                  <div key={c.domaine} className="flex flex-wrap items-baseline gap-2"
                       data-testid={`certificat-${c.domaine}`}>
                    <Badge className={teinteSysteme(c.statut)}>
                      {c.statut === 'inconnu' ? 'Illisible' : `${c.jours_restants} j`}
                    </Badge>
                    <span className="text-sm text-slate-700">{c.domaine}</span>
                    {c.expire_le && (
                      <span className="text-xs text-slate-400">
                        expire le {dateCourte(c.expire_le)}
                      </span>
                    )}
                  </div>
                ))}
                <p className="pt-1 text-xs text-slate-400">
                  {LIBELLES_VEILLE[systeme.veille_tuya] || systeme.veille_tuya}
                </p>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
